import {
  confirmSignUp,
  resendSignUpCode,
  configureAuth,
  getCurrentPoolId,
  signOut,
} from '@lastbrand/fe-common/universal-auth'
import { useTranslation } from 'next-i18next'
import { useCallback, useMemo, useRef, useState } from 'react'

import { FALLBACK_REGION_ID } from '@src/config/localisation/locale-config.constants'
import { REGION_ID_COOKIE } from '@src/config/localisation/locale-config.static-constants'
import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import {
  type IdentityConfigApiResponse,
  fetchIdentityConfigByEmail,
  mapIdentityConfigToAuthConfig,
} from '@src/modules/auth/identity-config-api'
import { recordIdentityConfigFetch } from '@src/modules/auth/identity-config-cache'
import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import SignupOTPModal from '@src/pages/sign-up/sign-up-otp-modal/SignupOTPModal'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent, trackInternalEvent } from '@src/telemetry/track'
import { getStandardLocaleFromCookie, parseLocale } from '@src/utils/i18n.utils'

import useLoginUser from '../../../../hooks/useLoginUser'
import { getSanitizedRedirectUrls } from '../../../Auth/utils'
import FindMyOrderWidget from '../../Components/find-my-order-widget'

import { shouldShowFmoWidget } from './show-fmo-widget.utils'
import { InitialPage } from './Views/InitialPage'
import { SecondaryPage } from './Views/SecondaryPage'

interface LoginVariantAPropTypes {
  serverError?: { message: string }
  unverifiedAccount?: boolean
  resendVerificationEmail: (email?: string) => void
  verificationEmailSent?: boolean
  email: string
  register: (x: string, y: object) => object
  errors: Record<string, { message: string }>
  backToLogin: () => void
  loading?: boolean
  page: any
  pageState: 'INITIAL' | 'SECONDARY'
  login: (data: object) => void
  openResetPasswordModal: (email: string) => void
  isInResetPasswordFlow?: boolean
  /** SSR-computed B2B auth source — spread through to InitialPage/SecondaryPage. */
  isB2BAuthSource?: boolean
}

const LoginVariantA = (props: LoginVariantAPropTypes) => {
  const router = useRouter()
  const email = useRef<string | undefined>()
  const [pageState, setPageState] = useState('INITIAL')
  const [pageLoading, setPageLoading] = useState(false)
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false)
  const [otpError, setOtpError] = useState<string | null>(null)
  const [pageError, setPageError] = useState<string | null>(null)
  const [resendLoading, setResendLoading] = useState(false)
  const [verifyLoading, setVerifyLoading] = useState(false)
  const { t } = useTranslation('accountPage')
  // TODO-SSR: Add useLoginUser function back after fixing the provider for auth
  const { loginUser } = useLoginUser()

  // eslint-disable-next-line i18next/no-literal-string
  const enableFMOForGuestUser = useStatsigDynamicConfig<boolean>('enableFMOForGuestUser')

  const showFmoWidget = useMemo(
    () =>
      shouldShowFmoWidget(
        Boolean(enableFMOForGuestUser),
        typeof window === 'undefined' ? undefined : window.location.search,
      ),
    [enableFMOForGuestUser],
  )

  const onPageStateChange = (username: string) => {
    email.current = username
    setPageLoading(true)

    const regionIdCookie = getCookie(REGION_ID_COOKIE)
    const standardLocale = getStandardLocaleFromCookie()
    const regionId = regionIdCookie ? Number(regionIdCookie) : FALLBACK_REGION_ID

    if (!regionIdCookie) {
      const country = standardLocale ? parseLocale(standardLocale).country : undefined
      // @i18n-ignore
      captureException(new Error(`Fallback to FALLBACK_REGION_ID for login`), {
        module: 'UNKNOWN',
        feature: 'UNKNOWN',
        extraInfo: {
          // eslint-disable-next-line
          identifier: 'LoginVariantA_onPageStateChange',
          locale: standardLocale,
          country,
        },
      })
      // @i18n-ignore
      trackInternalEvent('FALLBACK_REGION_ID', {
        // eslint-disable-next-line
        identifier: 'LoginVariantA_onPageStateChange',
        locale: standardLocale,
        country,
      })
    }

    const proceedWithCognitoSignIn = (resolvedIdentityConfig?: IdentityConfigApiResponse) => {
      loginUser(username, '#')
        .then(response => {
          const needSignUpConfirmation = 'needSignUpConfirmation' in response && response.needSignUpConfirmation
          const resetPasswordRequired = 'resetPasswordRequired' in response && response.resetPasswordRequired

          trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
            apiName: `login`,
            status: `success`,
            context: {
              login_email: username,
              isSignedIn: Boolean(response?.isSignedIn),
              needSignUpConfirmation: Boolean(needSignUpConfirmation),
              resetPasswordRequired: Boolean(resetPasswordRequired),
            },
          })

          if (needSignUpConfirmation) {
            setIsOtpModalOpen(true)
            setPageLoading(false)
            return
          }

          if (resetPasswordRequired) {
            props.openResetPasswordModal(username)
            setPageLoading(false)
            return
          }

          if (!response.isSignedIn) {
            setPageState('SECONDARY')
          } else {
            if (resolvedIdentityConfig) {
              recordIdentityConfigFetch(regionId, username.trim().toLowerCase(), resolvedIdentityConfig)
            }
            setPageError((response as { codeMedium?: string }).codeMedium ?? null)
          }
          setPageLoading(false)
        })
        .catch((error: unknown) => {
          const errorCode = (error as { name?: string; message?: string } | undefined)?.name
          const errorMessage = (error as { message?: string } | undefined)?.message

          // Only genuinely unexpected probe errors go to Datadog. The codes handled below
          // (NotAuthorized → advance to password, UserNotFound → sign-up, etc.) are expected
          // control flow for this account-existence probe, not login failures.
          const isExpectedProbeError =
            errorCode === 'UserNotConfirmedException' ||
            errorCode === 'PasswordResetRequiredException' ||
            errorCode === 'LimitExceededException' ||
            errorCode === 'UserNotFoundException' ||
            errorCode === 'NotAuthorizedException'
          if (!isExpectedProbeError) {
            captureException(error, {
              module: MODULES.UNIVERSAL_AUTH,
              feature: FEATURES.UNIVERSAL_AUTH,
              additionalTags: {
                login_method: 'email',
                user_type: 'd2c',
                error_type: errorCode || 'email_probe_failed',
              },
            })
          }

          trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
            apiName: `login`,
            status: `failure`,
            context: {
              email: username,
              error: errorMessage,
              errorType: errorCode,
            },
          })

          if (errorCode === 'UserNotConfirmedException') {
            setIsOtpModalOpen(true)
            setPageLoading(false)
            void resendOtp()
          }
          if (errorCode === 'PasswordResetRequiredException') {
            props.openResetPasswordModal(username)
          }

          if (error && (error as { name: string }).name === 'LimitExceededException') {
            setIsOtpModalOpen(false)
            setPageError(t('verification_attempts_limit'))
          }
          if (errorCode === 'UserNotFoundException') {
            const redirectUri = getSanitizedRedirectUrls()
            // Put `source` first and redirecturi last so source=b2b survives to /sign-up and post-signup cookies still write.
            const source = new URLSearchParams(window.location.search).get('source')
            const sourceParam = source ? `source=${source}&` : ''
            const navigateTo =
              redirectUri && redirectUri !== ''
                ? `/sign-up?${sourceParam}userMail=${btoa(username)}&redirecturi=${redirectUri}`
                : `/sign-up/?${sourceParam}userMail=${btoa(username)}`

            router.replace(navigateTo)
          } else if (error && (error as { name: string }).name === 'NotAuthorizedException') {
            setPageState('SECONDARY')
          }
          setPageLoading(false)
        })
    }

    if (getUserAgnosticGlobalFeatureGatesFromMemory().multi_pool_auth_enabled) {
      fetchIdentityConfigByEmail(regionId, username)
        .then(apiResponse => {
          const currentPoolId = getCurrentPoolId()
          const targetPoolId = apiResponse.cognitoPoolId
          if (currentPoolId != null && targetPoolId === currentPoolId) {
            // Pool already matches API: no signOut/configureAuth — still run the same Cognito step as other paths.
            proceedWithCognitoSignIn(apiResponse)
            return
          }

          void signOut()
            .then(() => {
              const authConfig = mapIdentityConfigToAuthConfig(apiResponse)
              return configureAuth({
                ...authConfig,
                onError(err: Error, context: Record<string, unknown>) {
                  captureException(err, {
                    module: MODULES.UNIVERSAL_AUTH,
                    feature: FEATURES.UNIVERSAL_AUTH,
                    extraInfo: context,
                  })
                },
              })
            })
            .then(() => {
              proceedWithCognitoSignIn(apiResponse)
            })
            .catch((err: unknown) => {
              captureException(err as Error, {
                module: MODULES.UNIVERSAL_AUTH,
                feature: FEATURES.UNIVERSAL_AUTH,
                extraInfo: { username, regionId, type: 'API_FAILURE', API: 'AUTH_REINIT' },
              })
              setPageLoading(false)
            })
        })
        .catch(() => {
          setPageLoading(false)
          proceedWithCognitoSignIn()
        })
    } else {
      proceedWithCognitoSignIn()
    }
  }

  const onEmailEdit = useCallback(() => {
    setPageState('INITIAL')
  }, [])

  const verifyOtp = async (otp: string) => {
    try {
      setVerifyLoading(true)
      setOtpError(null)
      const confirmSignUpResponse = await confirmSignUp(email.current ?? '', otp)

      if (confirmSignUpResponse.isSignUpComplete) {
        setIsOtpModalOpen(false)
        // Redirect to login page or automatically log the user in
        setPageState('SECONDARY')
      }
    } catch (err) {
      captureException(err, {
        module: MODULES.UNIVERSAL_AUTH,
        feature: FEATURES.UNIVERSAL_AUTH,
        additionalTags: {
          login_method: 'otp',
          user_type: 'd2c',
          error_type: (err as any)?.name || (err as any)?.code || 'signup_confirm_failed',
        },
      })
      if ((err as any)?.name === 'CodeMismatchException') {
        setOtpError((err as any)?.message || t('incorrect_verification_code'))
      } else if ((err as any)?.name === 'ExpiredCodeException') {
        setOtpError((err as any)?.message || t('verification_code_expired'))
      } else {
        setOtpError((err as any)?.message || t('account_verification_failed'))
      }
    } finally {
      setVerifyLoading(false)
    }
  }

  const resendOtp = async () => {
    try {
      const standardLocale = getStandardLocaleFromCookie()
      const shouldIncludeLocale = typeof window !== 'undefined' && window.__debug?.isI18nEnabled && standardLocale
      setResendLoading(true)
      await resendSignUpCode(email.current ?? '', {
        ...(shouldIncludeLocale
          ? {
              locale: standardLocale,
            }
          : {}),
      })
      setResendLoading(false)
    } catch (err) {
      captureException(err, {
        module: MODULES.UNIVERSAL_AUTH,
        feature: FEATURES.UNIVERSAL_AUTH,
        additionalTags: {
          login_method: 'otp',
          user_type: 'd2c',
          error_type: (err as any)?.name || (err as any)?.code || 'signup_resend_failed',
        },
      })
      if (err && (err as any).name === 'LimitExceededException') {
        setIsOtpModalOpen(false)
        setPageError((err as Error).message || t('verification_attempts_limit'))
      } else {
        setOtpError((err as any)?.message || t('verification_resend_failed'))
      }
      setResendLoading(false)
    }
  }

  return (
    <>
      {!props.isInResetPasswordFlow && pageState === 'INITIAL' ? (
        <InitialPage
          {...props}
          onPageStateChange={onPageStateChange}
          loading={pageLoading || props.loading}
          email={email.current}
          error={pageError}
        />
      ) : (
        <SecondaryPage
          {...props}
          email={email.current}
          loading={pageLoading || props.loading}
          onEmailEdit={onEmailEdit}
          isInResetPasswordFlow={props.isInResetPasswordFlow}
          error={pageError}
        />
      )}

      {showFmoWidget && <FindMyOrderWidget />}

      <SignupOTPModal
        open={isOtpModalOpen}
        onClose={() => {
          setIsOtpModalOpen(false)
          setOtpError(null)
        }}
        email={email.current ?? ''}
        onSubmit={otp =>
          void verifyOtp(otp).catch((err: unknown) => {
            console.error('OTP verification error:', err)
          })
        }
        onResend={() =>
          void resendOtp().catch((err: unknown) => {
            console.error('OTP resend error:', err)
          })
        }
        error={otpError}
        loading={verifyLoading}
        resendLoading={resendLoading}
      />
    </>
  )
}

export { LoginVariantA }
